/**
 * AdvertisingEnquiryModal - lead capture for the Featured Business
 * program. Reached from:
 *   - "Enquire about advertising" CTA on /advertise landing page
 *   - "Advertise here" placeholder cards (empty ad slots across the
 *     suburb pages, read guides, profile pages, etc.)
 *
 * Posts to POST /api/advertising-enquiries which persists to Mongo and
 * emails home@buildpilot.com.au (commercial inbox per the Featured
 * Business Pack).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATES = [
  { code: 'SA', label: 'South Australia' },
  { code: 'NSW', label: 'New South Wales' },
  { code: 'VIC', label: 'Victoria' },
  { code: 'QLD', label: 'Queensland' },
  { code: 'WA', label: 'Western Australia' },
  { code: 'TAS', label: 'Tasmania' },
  { code: 'ACT', label: 'Australian Capital Territory' },
  { code: 'NT', label: 'Northern Territory' },
];

const CATEGORIES = [
  'Builder',
  'Tradie',
  'Supplier',
  'Professional service (architect, surveyor, conveyancer, etc.)',
  'Other',
];

const BUDGETS = [
  'Under $250 / month',
  '$250 - $500 / month',
  '$500 - $1,000 / month',
  '$1,000+ / month',
  'Not sure yet',
];

const AdvertisingEnquiryModal = ({ sourcePage, defaultState, onClose }) => {
  const [form, setForm] = useState({
    business_name: '',
    contact_name: '',
    email: '',
    phone: '',
    state: defaultState || '',
    business_category: '',
    website: '',
    monthly_budget: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.business_name.trim() || !form.contact_name.trim() || !form.email.trim() || !form.phone.trim() || !form.state) {
      setError('Please complete all required fields.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/advertising-enquiries`, {
        ...form,
        source_page: sourcePage || 'advertise-landing',
      });
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Sorry, something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="ad-enquiry-title"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(15, 23, 42, 0.45)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, overflowY: 'auto',
      }}
      data-testid="advertising-enquiry-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 520,
          background: '#ffffff',
          borderRadius: 16,
          padding: '24px 24px 20px',
          boxShadow: '0 24px 60px rgba(15, 23, 42, 0.25)',
          margin: 'auto',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', marginBottom: 12 }}>
          <h2 id="ad-enquiry-title" style={{ fontSize: 22, fontWeight: 600, color: '#0a2540', margin: 0, letterSpacing: '-0.01em' }}>
            {done ? 'Thanks - we&rsquo;ll be in touch' : 'Advertise with BuildPilot'}
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            style={{ border: 0, background: 'transparent', cursor: 'pointer', color: '#6b7280', fontSize: 22, lineHeight: 1, padding: '0 4px' }}
            data-testid="advertising-enquiry-close"
          >×</button>
        </div>

        {done ? (
          <div>
            <p style={{ margin: '0 0 12px', fontSize: 14, color: '#374151', lineHeight: 1.55 }}>
              George from the BuildPilot team will be in touch within one business day with the Featured Business Pack and next steps.
            </p>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: '#6b7280', lineHeight: 1.5 }}>
              If you need us urgently, call George on <a href="tel:+61431067485" style={{ color: '#1d8efe', textDecoration: 'none', fontWeight: 600 }}>0431 067 485</a>.
            </p>
            <button
              type="button"
              onClick={onClose}
              style={{ padding: '10px 16px', background: '#0a2540', color: '#fff', border: 0, borderRadius: 10, fontSize: 13.5, fontWeight: 600, cursor: 'pointer', width: '100%' }}
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} noValidate>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: '#6b7280', lineHeight: 1.5 }}>
              Tell us about your business and we&rsquo;ll send through the Featured Business Pack with packages tailored to your state.
            </p>

            <Field label="Business name *" value={form.business_name} onChange={upd('business_name')} required testid="ad-enquiry-business-name" />
            <Field label="Your name *" value={form.contact_name} onChange={upd('contact_name')} required testid="ad-enquiry-contact-name" />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Field label="Email *" type="email" value={form.email} onChange={upd('email')} required testid="ad-enquiry-email" />
              <Field label="Phone *" type="tel" value={form.phone} onChange={upd('phone')} required testid="ad-enquiry-phone" />
            </div>

            <Select label="State *" value={form.state} onChange={upd('state')} required testid="ad-enquiry-state">
              <option value="">Select a state</option>
              {STATES.map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
            </Select>

            <Select label="Business category" value={form.business_category} onChange={upd('business_category')} testid="ad-enquiry-category">
              <option value="">Select a category</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>

            <Field label="Website" value={form.website} onChange={upd('website')} testid="ad-enquiry-website" placeholder="https://" />

            <Select label="Monthly budget" value={form.monthly_budget} onChange={upd('monthly_budget')} testid="ad-enquiry-budget">
              <option value="">Optional</option>
              {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
            </Select>

            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#374151', marginBottom: 4, fontWeight: 500 }}>Anything else?</label>
              <textarea
                value={form.message}
                onChange={upd('message')}
                rows={3}
                placeholder="Tell us about your business and what you're hoping to achieve."
                data-testid="ad-enquiry-message"
                style={{
                  width: '100%', padding: '10px 12px', fontSize: 13.5,
                  border: '1px solid rgba(15, 23, 42, 0.15)', borderRadius: 10,
                  resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.45,
                }}
              />
            </div>

            {error && (
              <div role="alert" style={{ marginBottom: 12, padding: '10px 12px', background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 10, fontSize: 13 }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="ad-enquiry-submit"
              style={{
                width: '100%', height: 44,
                background: submitting ? '#94a3b8' : '#0a2540',
                color: '#ffffff', border: 0, borderRadius: 10,
                fontSize: 14, fontWeight: 600,
                cursor: submitting ? 'wait' : 'pointer',
              }}
            >
              {submitting ? 'Sending…' : 'Send enquiry'}
            </button>

            <p style={{ margin: '12px 0 0', fontSize: 11.5, color: '#9ca3af', textAlign: 'center', lineHeight: 1.5 }}>
              We&rsquo;ll only use these details to discuss your enquiry. See our <a href="/privacy" style={{ color: '#6b7280', textDecoration: 'underline' }}>privacy policy</a>.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

const inputStyle = {
  width: '100%', padding: '10px 12px', fontSize: 13.5,
  border: '1px solid rgba(15, 23, 42, 0.15)', borderRadius: 10,
  fontFamily: 'inherit', lineHeight: 1.4, background: '#ffffff',
};

const Field = ({ label, value, onChange, type = 'text', required, testid, placeholder }) => (
  <div style={{ marginBottom: 10 }}>
    <label style={{ display: 'block', fontSize: 12, color: '#374151', marginBottom: 4, fontWeight: 500 }}>{label}</label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      data-testid={testid}
      style={inputStyle}
    />
  </div>
);

const Select = ({ label, value, onChange, required, testid, children }) => (
  <div style={{ marginBottom: 10 }}>
    <label style={{ display: 'block', fontSize: 12, color: '#374151', marginBottom: 4, fontWeight: 500 }}>{label}</label>
    <select
      value={value}
      onChange={onChange}
      required={required}
      data-testid={testid}
      style={inputStyle}
    >
      {children}
    </select>
  </div>
);

export default AdvertisingEnquiryModal;
